export const REACT_DEV_MEASURE_PREFIX = '\u200B';
export const REACT_DEV_MEASURE_CLEANUP_THRESHOLD = 10_000;

export interface PerformanceMeasureApi {
  getEntriesByType: (type: string) => PerformanceEntry[];
  clearMeasures: (name?: string) => void;
}

interface MeasureObserver {
  observe: (options: PerformanceObserverInit) => void;
  disconnect: () => void;
}

interface ReactDevMeasureCleanupOptions {
  performanceApi?: PerformanceMeasureApi;
  observerFactory?: (onEntries: (entries: PerformanceEntry[]) => void) => MeasureObserver;
  schedule?: (callback: () => void) => ReturnType<typeof setTimeout>;
  cancelSchedule?: (handle: ReturnType<typeof setTimeout>) => void;
  threshold?: number;
}

export function isReactDevMeasure(entry: Pick<PerformanceEntry, 'entryType' | 'name'>) {
  return entry.entryType === 'measure' && entry.name.startsWith(REACT_DEV_MEASURE_PREFIX);
}

function countReactDevMeasures(performanceApi: PerformanceMeasureApi) {
  return performanceApi.getEntriesByType('measure').filter(isReactDevMeasure).length;
}

export function clearReactDevMeasures(performanceApi: PerformanceMeasureApi = performance) {
  const entries = performanceApi.getEntriesByType('measure').filter(isReactDevMeasure);
  const names = new Set(entries.map(entry => entry.name));
  names.forEach(name => performanceApi.clearMeasures(name));
  return { measures: entries.length, names: names.size };
}

export function createReactDevMeasureCleanup(options: ReactDevMeasureCleanupOptions = {}) {
  const performanceApi = options.performanceApi ?? performance;
  const observerFactory = options.observerFactory ?? (onEntries => new PerformanceObserver((list) => {
    onEntries(list.getEntries());
  }));
  const schedule = options.schedule ?? (callback => setTimeout(callback, 0));
  const cancelSchedule = options.cancelSchedule ?? (handle => clearTimeout(handle));
  const threshold = options.threshold ?? REACT_DEV_MEASURE_CLEANUP_THRESHOLD;

  let observer: MeasureObserver | null = null;
  let scheduledCleanup: ReturnType<typeof setTimeout> | null = null;
  let reactMeasureCount = 0;

  const runCleanup = () => {
    scheduledCleanup = null;
    clearReactDevMeasures(performanceApi);
    reactMeasureCount = countReactDevMeasures(performanceApi);
  };

  const scheduleCleanupIfNeeded = () => {
    if (reactMeasureCount < threshold || scheduledCleanup !== null) {
      return;
    }
    scheduledCleanup = schedule(runCleanup);
  };

  const syncCount = () => {
    reactMeasureCount = countReactDevMeasures(performanceApi);
    if (reactMeasureCount < threshold && scheduledCleanup !== null) {
      cancelSchedule(scheduledCleanup);
      scheduledCleanup = null;
      return;
    }
    scheduleCleanupIfNeeded();
  };

  const start = () => {
    if (observer !== null) {
      return;
    }

    syncCount();
    observer = observerFactory((entries) => {
      reactMeasureCount += entries.filter(isReactDevMeasure).length;
      scheduleCleanupIfNeeded();
    });
    observer.observe({ type: 'measure' });
  };

  const stop = () => {
    observer?.disconnect();
    observer = null;
    if (scheduledCleanup !== null) {
      cancelSchedule(scheduledCleanup);
      scheduledCleanup = null;
    }
    reactMeasureCount = 0;
  };

  return { start, stop, syncCount };
}
